// src/shared/session/SessionTimeoutProvider.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import SessionTimeoutModal from "./sessionTimeoutProvider/SessionTimeoutModal";
import { logout, getToken, isTokenExpired } from "../lib/auth/authHelper";
import appConfig from "../../config/appConfig";
import api from "../api/apiClient"; // ✅ axios instance (withCredentials:true)

const SessionTimeoutContext = createContext(null);

// ✅ дефолты берём из appConfig
const DEFAULTS = {
  warningMs: appConfig.SESSION_WARNING_MS ?? 60_000,
  extendByMs: appConfig.SESSION_EXTEND_BY_MS ?? 15 * 60_000,
};

// (опционально) оставляем ключ для dev-тестов/фоллбэка
const OVERRIDE_EXP_KEY = "session-exp-ms";

function getExpMsFromToken(token) {
  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return null;
    return decoded.exp * 1000;
  } catch {
    return null;
  }
}

function getOverrideExpMs() {
  const raw = localStorage.getItem(OVERRIDE_EXP_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : null;
}
function setOverrideExpMs(ms) {
  localStorage.setItem(OVERRIDE_EXP_KEY, String(ms));
}
function clearOverrideExpMs() {
  localStorage.removeItem(OVERRIDE_EXP_KEY);
}

// ✅ редирект на логин (у тебя логин это "/")
function goLogin(reason = "expired") {
  const loginPath = appConfig.LOGIN_PATH || "/";
  window.location.assign(`${loginPath}?reason=${encodeURIComponent(reason)}`);
}

export function useSessionTimeout() {
  return useContext(SessionTimeoutContext);
}

export default function SessionTimeoutProvider({
  children,
  warningMs = DEFAULTS.warningMs,
  extendByMs = DEFAULTS.extendByMs,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [busy, setBusy] = useState(false);

  const timersRef = useRef({ warn: null, logout: null, ticker: null });

  const clearTimers = useCallback(() => {
    const t = timersRef.current;
    if (t.warn) clearTimeout(t.warn);
    if (t.logout) clearTimeout(t.logout);
    if (t.ticker) clearInterval(t.ticker);
    timersRef.current = { warn: null, logout: null, ticker: null };
  }, []);

  const forceLogout = useCallback(
    (reason = "expired") => {
      clearTimers();
      setModalOpen(false);
      setSecondsLeft(null);
      clearOverrideExpMs();
      logout();
      goLogin(reason);
    },
    [clearTimers]
  );

  // ✅ возвращает “эффективный exp” (JWT exp или override если вдруг юзаешь)
  const getEffectiveExpMs = useCallback(() => {
    const token = getToken();
    const tokenExp = token ? getExpMsFromToken(token) : null;
    const overrideExp = getOverrideExpMs();

    const exp = Math.max(tokenExp || 0, overrideExp || 0);

    if (!exp) return null;
    if (exp <= Date.now()) return "expired";

    return exp;
  }, []);


  const schedule = useCallback(() => {
    clearTimers();

    const effectiveExp = getEffectiveExpMs();
    if (!effectiveExp) return;

    if (effectiveExp === "expired") {
      forceLogout("expired");
      return;
    }

    const msLeft = effectiveExp - Date.now();
    if (msLeft <= 0) {
      forceLogout("expired");
      return;
    }

    const warnAt = Math.max(0, msLeft - warningMs);

    // countdown
    timersRef.current.ticker = setInterval(() => {
      const eff = getEffectiveExpMs();
      if (!eff || eff === "expired") return;

      const left = Math.max(0, Math.floor((eff - Date.now()) / 1000));
      setSecondsLeft(left);
    }, 500);

    // показать модалку
    timersRef.current.warn = setTimeout(() => {
      setModalOpen(true);
    }, warnAt);

    // логаут
    timersRef.current.logout = setTimeout(() => {
      forceLogout("expired");
    }, msLeft);
  }, [clearTimers, forceLogout, getEffectiveExpMs, warningMs]);

  const resync = useCallback(() => schedule(), [schedule]);

  /**
   * ✅ ВАЖНО:
   * Продление = запрос на BE /auth/refresh.
   * BE вернёт новый access_token + обновит httpOnly cookie.
   * Мы сохраняем новый токен в localStorage и пересчитываем таймеры.
   */
  const extendSession = useCallback(async () => {
    if (busy) return;
    setBusy(true);

    try {
      const res = await api.post(
        "/auth/refresh",
        {},
        {
          withCredentials: true,
        }
      );

      const newToken = res?.data?.access_token;
      if (!newToken) throw new Error("No access_token in refresh response");

      localStorage.setItem("token", newToken);
      clearOverrideExpMs();
      setModalOpen(false);

      window.dispatchEvent(new Event("token-changed"));
      schedule();
    } catch (e) {
      console.error("[session refresh failed]", e);
      console.error("[session refresh status]", e?.response?.status);
      console.error("[session refresh data]", e?.response?.data);

      const now = Date.now();
      setOverrideExpMs(now + extendByMs);
      setModalOpen(false);
      schedule();
    } finally {
      setBusy(false);
    }
  }, [busy, extendByMs, schedule]);

  useEffect(() => {
    schedule();

    const onStorage = (ev) => {
      if (ev.key === "token" || ev.key === OVERRIDE_EXP_KEY) schedule();
    };

    const onTokenChanged = () => {
      // при новом логине/рефреше
      clearOverrideExpMs();
      schedule();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("token-changed", onTokenChanged);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("token-changed", onTokenChanged);
      clearTimers();
    };
  }, [schedule, clearTimers]);

  const value = useMemo(
    () => ({ resync, forceLogout, extendSession }),
    [resync, forceLogout, extendSession]
  );

  return (
    <SessionTimeoutContext.Provider value={value}>
      {children}

      <SessionTimeoutModal
        open={modalOpen}
        secondsLeft={secondsLeft}
        onExtend={extendSession}
        onLogout={() => forceLogout("user_logout")}
        busy={busy}
        extendByMs={extendByMs}
      />
    </SessionTimeoutContext.Provider>
  );
}
